import { useEffect } from 'react';
import { buildCanonicalUrl, buildPageTitle, siteMeta } from '../seo/siteMeta';

const MANAGED_META_ATTR = 'data-seo-managed';
const MANAGED_SCHEMA_ATTR = 'data-seo-jsonld';

const upsertMetaByName = (name, content) => {
  if (!content) return;

  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
  tag.setAttribute(MANAGED_META_ATTR, 'true');
};

const upsertMetaByProperty = (property, content) => {
  if (!content) return;

  let tag = document.head.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
  tag.setAttribute(MANAGED_META_ATTR, 'true');
};

const upsertCanonical = (href) => {
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
  tag.setAttribute(MANAGED_META_ATTR, 'true');
};

const clearManagedStructuredData = () => {
  const previousScripts = document.head.querySelectorAll(`script[${MANAGED_SCHEMA_ATTR}="true"]`);
  previousScripts.forEach((script) => script.remove());
};

const addStructuredData = (entries = []) => {
  entries.forEach((entry) => {
    if (!entry) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(MANAGED_SCHEMA_ATTR, 'true');
    script.textContent = JSON.stringify(entry);
    document.head.appendChild(script);
  });
};

const SEO = ({
  title,
  description,
  path = '/',
  imagePath = siteMeta.logoPath,
  type = 'website',
  structuredData = [],
  noindex = false
}) => {
  useEffect(() => {
    const fullTitle = buildPageTitle(title);
    const metaDescription = description || siteMeta.defaultDescription;
    const canonicalUrl = buildCanonicalUrl(path);
    const imageUrl = imagePath ? buildCanonicalUrl(imagePath) : undefined;

    document.title = fullTitle;

    upsertMetaByName('description', metaDescription);
    upsertMetaByName('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    upsertMetaByName('twitter:card', 'summary_large_image');
    upsertMetaByName('twitter:title', fullTitle);
    upsertMetaByName('twitter:description', metaDescription);
    if (imageUrl) {
      upsertMetaByName('twitter:image', imageUrl);
    }

    upsertMetaByProperty('og:type', type);
    upsertMetaByProperty('og:site_name', siteMeta.siteName);
    upsertMetaByProperty('og:title', fullTitle);
    upsertMetaByProperty('og:description', metaDescription);
    upsertMetaByProperty('og:url', canonicalUrl);
    if (imageUrl) {
      upsertMetaByProperty('og:image', imageUrl);
    }

    upsertCanonical(canonicalUrl);

    clearManagedStructuredData();
    addStructuredData(structuredData);
  }, [title, description, path, imagePath, type, noindex, structuredData]);

  return null;
};

export default SEO;
